import React from 'react'
import { Button } from '@/components/ui/button'
import { NameDto } from 'api/Common/Types'
import { SubscriptionDetailDto } from 'api/Trading-Accounts/Types/SubscriptionDetailsDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

export interface SubscriptionsDetailsMainValues {
  id: string | undefined
  subscriptionId: string
  name: string
  description: string
  detailTypeId: number
  amount: number
  mappingId: string
  mappingText: string
}

export const SubscriptionsDetailsMainFormUI: React.FC<
  FormikProps<SubscriptionsDetailsMainValues> & OuterProps
> = (props) => {
  const { tradingAccountSubscriptionTypes, handleSubmit, errors, isSubmitting } = props

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form onSubmit={handleSubmitForm}>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Name</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='name' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.name && <FormBaseErrorMessage>{errors.name}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Description</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='description' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.description && <FormBaseErrorMessage>{errors.description}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Type</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='detailTypeId'>
                <option value=''>None</option>
                {tradingAccountSubscriptionTypes.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.detailTypeId && (
            <FormBaseErrorMessage>{errors.detailTypeId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='amount' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.amount && <FormBaseErrorMessage>{errors.amount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Mapping Id</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='mappingId' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.mappingId && <FormBaseErrorMessage>{errors.mappingId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Mapping Text</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field name='mappingText' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.mappingText && <FormBaseErrorMessage>{errors.mappingText}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} type='submit'>
            Save
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  subscription: SubscriptionDetailDto | undefined
  tradingAccountSubscriptionTypes: NameDto[]
  isNew?: boolean
  onSubmit(values: FormSubmitValues<SubscriptionsDetailsMainValues>): Promise<void>
}

const initialSubscriptionDetailState = (
  subscription: SubscriptionDetailDto | undefined
): SubscriptionsDetailsMainValues => {
  return {
    id: subscription?.id,
    subscriptionId: subscription?.subscriptionId || '',
    name: subscription?.name || '',
    description: subscription?.description || '',
    detailTypeId: subscription?.detailType?.id || 0,
    amount: subscription?.amount || 0,
    mappingId: subscription?.mappingId || '',
    mappingText: subscription?.mappingText || '',
  }
}

export const SubscriptionsDetailsMainForm = withFormik<OuterProps, SubscriptionsDetailsMainValues>({
  mapPropsToValues: ({ subscription }) => {
    return initialSubscriptionDetailState(subscription)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<SubscriptionsDetailsMainValues> = {}

    if (!values?.name) {
      errors.name = 'Required'
    }

    if (!values?.description) {
      errors.description = 'Required'
    }

    if (!values?.detailTypeId) {
      errors.detailTypeId = 'Required'
    }

    if (!values?.amount) {
      errors.amount = 'Required'
    }

    if (!values?.mappingId) {
      errors.mappingId = 'Required'
    }

    if (!values?.mappingText) {
      errors.mappingText = 'Required'
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(SubscriptionsDetailsMainFormUI)
